import { gql, useMutation, useQuery } from '@apollo/client';
import { Button, Callout, DataList, Dialog, DropdownMenu, IconButton, Link } from '@radix-ui/themes';
import { Ellipsis } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Button as HubertButton } from '@hubertai/hubert-component-library';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';
import { APPLICATION_SUMMARY_FRAGMENT } from '../ApplicationFragments';
import { capitalizeName, getStatusObject } from '../common';
import Title from '../elements/Title';
import ActivityLog from '../swiper/ActivityLog';
import ActivityLogSkeleton from '../swiper/ActivityLogSkeleton';
import Header from '../swiper/Header';
import type { Application } from '../swiper/Header';
import Summary from '../swiper/Summary';
import { ApplicationNotes } from './ApplicationNotes';

const CANDIDATE_QUERY = gql`
  query CANDIDATE_QUERY($candidateId: String) {
    candidate(candidateId: $candidateId) {
      id
      firstName
      lastName
      email
      createdAt
      phoneNumber
      applications {
        totalCount
        nodes {
          id
          createdAt
          status
          score
          stage
          accepted
          job {
            title
            id
            threshold
            company
            integration {
              teamtailor {
                jobId
                companyId
              }
            }
          }
          interviews {
            nodes {
              id
            }
          }
          integration {
            teamtailor {
              jobId
              candidateId
            }
          }
          candidate {
            id
            firstName
            lastName
            email
            createdAt
            phoneNumber
          }
          activityLog {
            createdAt
            status
            stage
            actionType
            actionSubType
            message
          }
          notes {
            createdAt
            createdBy
            text
          }
          documents {
            id
            type
          }
          ...Application_summaries
        }
      }
    }
  }
  ${APPLICATION_SUMMARY_FRAGMENT}
`;

const UPDATE_APPLICATION = gql`
  mutation UPDATE_APPLICATION($applicationId: String!, $newStatus: String!) {
    applicationMutation(input: { applicationId: $applicationId, newStatus: $newStatus }) {
      id
      createdAt
      status
      score
      stage
      accepted
      activityLog {
        createdAt
        status
        stage
        actionType
        message
      }
    }
  }
`;

const APPLICATION_ADD_NOTE = gql`
  mutation AddNote($applicationId: String!, $text: String!) {
    addApplicationNote(input: { applicationId: $applicationId, text: $text }) {
      id
      createdAt
      status
      score
      stage
      accepted
      activityLog {
        createdAt
        status
        stage
        actionType
        message
      }
    }
  }
`;

const DELETE_APPLICATION = gql`
  mutation DELETE_APPLICATION($deleteApplicationInput: DeleteApplicationInput!) {
    deleteApplication(input: $deleteApplicationInput)
  }
`;

const DELETE_CANDIDATE = gql`
  mutation DELETE_CANDIDATE($DeleteCandidateInput: DeleteCandidateInput!) {
    deleteCandidate(input: $DeleteCandidateInput)
  }
`;

const DeleteCandidateModal = ({
  show,
  onHide,
  deleteCandidate,
}: {
  show: boolean;
  onHide: (hide: boolean) => void;
  deleteCandidate: { deleteCandidateCallback: () => void; loading: boolean };
}) => {
  const { t } = useTranslation();
  const { deleteCandidateCallback, loading } = deleteCandidate;

  return (
    <Dialog.Root open={show} onOpenChange={() => onHide(false)}>
      <Dialog.Content>
        <Dialog.Title>{t('candidate.deleteDialog.title')}</Dialog.Title>
        <Dialog.Description>
          <p>{t('candidate.deleteDialog.warning')}</p>
          <p>{t('candidate.deleteDialog.note')}</p>
        </Dialog.Description>
        <div className="flex">
          <div className="w-1/2 px-4">
            <Button
              color="gray"
              size="3"
              variant="soft"
              className="w-full"
              disabled={loading}
              onClick={() => onHide(false)}
            >
              {t('candidate.deleteDialog.cancelButton')}
            </Button>
          </div>
          <div className="w-1/2 px-4">
            <Button color="red" size="3" className="w-full" loading={loading} onClick={() => deleteCandidateCallback()}>
              {t('candidate.deleteDialog.confirmButton')}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const Candidates = () => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);
  const { candidateId, applicationId } = useParams<{ candidateId: string; applicationId: string }>();
  const navigate = useNavigate();

  const {
    data,
    error,
    loading,
    refetch: refetchCandidate,
  } = useQuery<{
    candidate: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      applications: {
        nodes: Array<Application | undefined>;
      };
    };
  }>(CANDIDATE_QUERY, {
    variables: {
      candidateId: candidateId,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (data && data.candidate) {
      const applicationIndex = data.candidate.applications.nodes.findIndex((app) => app && app.id === applicationId);
      if (applicationIndex >= 0) setSelected(applicationIndex);
    }
  }, [data, applicationId]);

  const [updateApplication] = useMutation(UPDATE_APPLICATION);

  const [deleteApplicationMutation, { loading: deleteApplicationLoading }] = useMutation(DELETE_APPLICATION);

  const deleteApplication = async () => {
    if (data && data.candidate.applications.nodes[selected])
      await deleteApplicationMutation({
        variables: {
          deleteApplicationInput: {
            applicationId: data.candidate.applications.nodes[selected].id,
          },
        },
        update(cache) {
          if (data.candidate.applications.nodes[selected]) {
            cache.evict({
              id: cache.identify(data.candidate.applications.nodes[selected]),
            });

            if (data.candidate.applications.nodes.length <= 1) {
              cache.evict({
                id: cache.identify(data.candidate),
              });
              navigate('');
            }
          }
        },
      });
  };

  const [deleteCandidateMutation, { loading: deleteCandidateLoading }] = useMutation(DELETE_CANDIDATE);

  const deleteCandidate = async () => {
    if (data)
      await deleteCandidateMutation({
        variables: {
          DeleteCandidateInput: {
            candidateId: data.candidate.id,
          },
        },
        update(cache) {
          cache.evict({
            id: cache.identify(data.candidate),
          });

          navigate('');
        },
      });
  };

  const deleteCandidateProps = {
    deleteCandidateCallback: deleteCandidate,
    loading: deleteCandidateLoading,
  };

  const deleteApplicationProps = {
    deleteApplicationCallback: deleteApplication,
    loading: deleteApplicationLoading,
  };

  const updateStatusOnApplication = (e: { type: string; status: string }) => {
    if (data && data.candidate.applications.nodes[selected]) {
      track(AnalyticsEvents.CANDIDATE_STATUS_CHANGED, { to_status: e.status, from: 'profile' });
      const status = getStatusObject(e.status);
      updateApplication({
        variables: {
          applicationId: data.candidate.applications.nodes[selected].id,
          newStatus: e.status,
        },
        optimisticResponse: {
          applicationMutation: {
            id: data.candidate.applications.nodes[selected].id,
            __typename: 'Application',
            status: e.status,
            stage: status?.stage,
            ...(status && 'accepted' in status && { accepted: status?.accepted }),
          },
        },
      });
    }

    return null;
  };

  const updateSelected = (id: string) => {
    if (data) {
      const applications = data.candidate.applications.nodes;
      const test = applications.findIndex((application) => application && application.id === id);
      setSelected(test ? test : 0);
    }
  };

  const [addApplicationNote] = useMutation(APPLICATION_ADD_NOTE);
  const addNote = async ({ text }: { text: string }) => {
    if (data && data.candidate.applications.nodes[selected]) {
      console.info('addNote', { text });
      const applicationId = data.candidate.applications.nodes[selected].id;
      await addApplicationNote({
        variables: {
          applicationId: applicationId,
          text,
        },
      });
      // Never the note text.
      track(AnalyticsEvents.NOTE_ADDED);
      refetchCandidate();
    }
  };

  if (data && !data.candidate) {
    return (
      <Callout.Root color="red" className="justify-center">
        <Callout.Text className="mb-0">{t('candidate.notFound')}</Callout.Text>
      </Callout.Root>
    );
  } else if (error) {
    return (
      <Callout.Root color="red" className="justify-center">
        <Callout.Text className="mb-0">{t('candidate.notFound')}</Callout.Text>
      </Callout.Root>
    );
  }

  return (
    <div className="mt-2 candidate">
      <Title
        title={
          data && data.candidate
            ? `${capitalizeName(data.candidate.firstName)} ${capitalizeName(data.candidate.lastName)}`
            : t('candidate.titleFallback')
        }
      />
      <div className="swiper-wrap flex">
        <div className="w-[25%]!">
          {data && (
            <CandidateContact
              candidate={data.candidate}
              selected={selected}
              selectedCallback={updateSelected}
              deleteCandidate={deleteCandidateProps}
              addNote={addNote}
            />
          )}
        </div>

        <div className="swiper-body w-[75%]!">
          {!loading && data && data.candidate && (
            <>
              <Header
                application={data.candidate.applications.nodes[selected]}
                onStatusChange={updateStatusOnApplication}
                deleteApplication={deleteApplicationProps}
              />
              <Summary
                className={'swiper-actionbar-offset'}
                summaries={data.candidate.applications.nodes[selected]?.summaries.nodes ?? []}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Candidates;

const CandidateContact = ({
  candidate,
  selected,
  selectedCallback,
  deleteCandidate,
  addNote,
}: {
  candidate: {
    email: string;
    phoneNumber: string;
    applications: {
      nodes: (Application | undefined)[];
    };
  };
  selected: number;
  selectedCallback: (id: string) => void;
  deleteCandidate: {
    deleteCandidateCallback: () => void;
    loading: boolean;
  };
  addNote: (note: { text: string }) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const applications = candidate ? candidate.applications.nodes : null;
  const [deleteCandModalShow, setDeleteCandModalShow] = useState(false);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  return (
    <>
      <DeleteCandidateModal
        deleteCandidate={deleteCandidate}
        show={deleteCandModalShow}
        onHide={setDeleteCandModalShow}
      />
      <div className="flex items-center justify-between">
        <h2 className="mb-0">{t('candidate.contact.heading')}</h2>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <IconButton color="gray" variant="ghost" size="1" className="m-0" highContrast>
              <Ellipsis size={20} />
            </IconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content color="gray" variant="soft" align="end">
            <DropdownMenu.Item color="red" onClick={() => setDeleteCandModalShow(true)}>
              {t('candidate.contact.deleteMenuItem')}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
      <DataList.Root className="pl-1 mt-1">
        <DataList.Item>
          <DataList.Label className="font-bold" minWidth="50px">
            {t('candidate.contact.phoneLabel')}
          </DataList.Label>
          <DataList.Value>
            {candidate && candidate.phoneNumber ? (
              <Link href={`tel:${candidate.phoneNumber}`}>{candidate.phoneNumber}</Link>
            ) : (
              '-'
            )}
          </DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <DataList.Label className="font-bold" minWidth="50px">
            {t('candidate.contact.emailLabel')}
          </DataList.Label>
          <DataList.Value>
            {candidate && candidate.email ? <Link href={`mailto:${candidate.email}`}>{candidate.email}</Link> : '-'}
          </DataList.Value>
        </DataList.Item>
      </DataList.Root>

      {applications && applications.length > 0 && (
        <>
          <ActivityLog
            application={candidate.applications.nodes[selected]}
            preApplications={candidate.applications.nodes}
            selectedCallback={selectedCallback}
            selected={selected}
          />
          <div className="flex flex-col gap-2">
            <ApplicationNotes
              notes={candidate.applications.nodes[selected] ? candidate.applications.nodes[selected].notes : []}
            />
            <div className="px-1 my-1">
              <textarea className="w-full border border-gray-200" ref={noteRef} name="note" rows={5}></textarea>
              <div className="flex flex-row justify-end">
                <HubertButton
                  className=""
                  size="1"
                  variant="outline"
                  type="submit"
                  onClick={async (e) => {
                    e.preventDefault();
                    const note = noteRef.current;
                    if (!note) return;

                    note.disabled = true;
                    if (note.value) await addNote({ text: note.value });
                    note.disabled = false;
                    note.value = '';
                  }}
                >
                  {t('candidate.contact.addNoteButton')}
                </HubertButton>
              </div>
            </div>
          </div>
        </>
      )}
      {!applications && <ActivityLogSkeleton />}
    </>
  );
};
